import { format, isValid } from "date-fns";
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  JSXElement,
  Show,
} from "solid-js";

import { onKeyDown } from "../../utils/inputUtils.js";
import { DateValue } from "../common/DateValue.js";
import { IssueDisplay, useForm } from "./FormContext.js";

interface DateInputProps {
  value?: string | null;
  editable?: boolean;
  name?: string;
  onChange?: (value: string | null) => void;
  class?: string;
  label?: string | JSXElement;
  labelClass?: string;
  placeholder?: string;
  transformer?: (value: Date) => string;
}

const DateInput: Component<DateInputProps> = (props) => {
  const [day, setDay] = createSignal<string>("");
  const [month, setMonth] = createSignal<string>("");
  const [year, setYear] = createSignal<string>("");

  const commonClassName = createMemo(
    () =>
      `border rounded-md text-gray-500 ${
        props.editable === false ? "" : "p-2"
      } ${props.class}`,
  );
  const labelClass = createMemo(
    () => `text-gray-500 ${props.labelClass ?? ""}`,
  );
  const id = createUniqueId();
  const componentId = createUniqueId();

  const formContext = useForm();
  const relevantIssues = () => formContext?.getIssues(props.name);

  const getValue = () => {
    if (formContext && props.name) {
      return formContext.getData(props.name) ?? null;
    }
    return props.value ?? null;
  };

  createEffect(() => {
    const currentValue = getValue();
    if (currentValue === null) {
      setDay("");
      setMonth("");
      setYear("");
      return;
    }
    if (currentValue === "") {
      return;
    }
    const date = new Date(currentValue);
    setDay(date.getDate().toString());
    setMonth((date.getMonth() + 1).toString());
    setYear(date.getFullYear().toString());
  });

  const valuePresentOrEmpty = () => {
    if (!day() && !month() && !year()) {
      return true;
    }
    if (day().length && month().length && year().length) {
      return true;
    }
    return false;
  };

  const valueChanged = () => {
    let newValue: string | null = null;
    const newDate = new Date();
    const dateValues = [year(), month(), day()];
    try {
      if (!valuePresentOrEmpty()) {
        return;
      }
      const numericDateValues = dateValues.map((value) => {
        const parsed = Number.parseInt(value);
        if (Number.isNaN(parsed)) {
          throw new TypeError("incorrect number");
        }
        return parsed;
      });

      newDate.setFullYear(numericDateValues[0]);
      newDate.setMonth(numericDateValues[1] - 1);
      newDate.setDate(numericDateValues[2]);

      if (isValid(newDate)) {
        newValue = props.transformer
          ? props.transformer(newDate)
          : format(newDate, "yyyy-MM-dd");
      } else {
        newValue = null;
      }
    } catch {
      newValue = null;
    }

    if (formContext && props.name) {
      formContext.setData(props.name, newValue);
    }
    props.onChange?.(newValue);
  };

  const displayableValue = () => {
    const currentValue = getValue();
    if (!currentValue) {
      return props.placeholder;
    }
    return currentValue;
  };

  const getFormErrors = () => {
    if (!props.name) {
      return false;
    }

    const errors = formContext?.getErrors(props.name);
    // sometimes this returns true, which means the field is valid
    if (errors && errors !== true) {
      return errors;
    }
    return formContext?.errors?.()?.[props.name] ?? false;
  };

  return (
    <div id={componentId} class="flex flex-col">
      <Show when={props.label}>
        <label for={id} class={labelClass()}>
          {props.label}
        </label>
      </Show>
      <Show
        when={props.editable || props.editable === undefined}
        fallback={
          <DateValue
            date={displayableValue()}
            class={`border-transparent ${commonClassName()}`}
            fallback={props.placeholder}
          />
        }
      >
        <div
          class="grid w-full grid-cols-4 gap-2"
          classList={{
            "[&>*]:border-error-500 [&>*]:outline-error-500":
              Boolean(getFormErrors()) || Boolean(relevantIssues()?.length),
          }}
        >
          <input
            onKeyDown={onKeyDown}
            class={commonClassName()}
            value={day() ?? ""}
            placeholder="DD"
            onChange={(e) => {
              setDay(e.currentTarget.value);
              valueChanged();
            }}
          />
          <input
            onKeyDown={onKeyDown}
            class={commonClassName()}
            value={month() ?? ""}
            placeholder="MM"
            onChange={(e) => {
              setMonth(e.currentTarget.value);
              valueChanged();
            }}
          />
          <input
            onKeyDown={onKeyDown}
            class={`col-span-2 ${commonClassName()}`}
            value={year() ?? ""}
            placeholder="YYYY"
            onChange={(e) => {
              setYear(e.currentTarget.value);
              valueChanged();
            }}
          />
        </div>
        <Show when={relevantIssues()}>
          <p class="text-sm text-red-500 lg:col-span-2 lg:col-start-2">
            <IssueDisplay issues={relevantIssues()} />
          </p>
        </Show>
        <Show when={getFormErrors() !== false}>
          <p class="text-sm text-error-500 lg:col-span-2 lg:col-start-2">
            <For each={getFormErrors()}>{(error) => <p>{error}</p>}</For>
          </p>
        </Show>
      </Show>
    </div>
  );
};

export default DateInput;
